<template>
  <div class="sleepdebt">
    <div class="wrap">
      <!-- Title -->
      <h1 class="title">What is Sleep Debt?</h1>

      <!-- Sleep Debt bar -->
      <div class="debt-bar">
        <div class="accent" aria-hidden="true"></div>

        <div class="debt-row">
          <div class="left">
            <div class="bed-ic" aria-hidden="true">
               <IconBed />
            </div>
            <div class="label">Sleep Debt</div>
          </div>

          <div class="right">
            <div class="value">1h 10m</div>
            <div class="range">30m – 1h 30m</div>
          </div>
        </div>
      </div>

      <!-- Paragraphs -->
      <p class="text-sm accordion-body text-lighter opacity-8">
        Sleep debt refers to the difference between the amount of sleep you need
        and the amount you get. Over time, if you don’t get enough sleep for
        continuous days, sleep debt builds up. Accumulating a high level of
        sleep debt can harm your physical and mental health.
      </p>

      <p class="text-sm accordion-body text-lighter opacity-8">
        To perform at your best, aim to keep your sleep debt under 30 minutes.
        Getting extra sleep or taking naps can help reduce your sleep debt.
        Depending on how much debt you’ve accumulated, it may take several
        nights of sufficient sleep to fully recover.
      </p>

      <!-- Sleep Need calculated -->
      <h2 class="sub-title">How is sleep Need calculated?</h2>

      <div class="calc-card">
        <div class="calc-row">
          <div class="w-15">
            <span class="square bg-osd me-2"></span>
            Your OSD
          </div>
          <div class="v">8:10</div>
        </div>

        <div class="calc-row">
          <div class="w-15">
            <span class="square bg-strain me-2"></span>
             Yesterday's Strain 
          </div>
          <div class="v">+0:15</div>
        </div>

        <div class="calc-row">
          <div class="w-15">
            <span class="square bg-debt me-2"></span>
            Recent Sleep Debt
          </div>
          <div class="v">+1:35</div>
        </div>
      </div>

      <p class="text-sm accordion-body text-lighter opacity-8">
        Sleep need is calculated daily based on your Optimal Sleep Duration
        (OSD), strain from the previous day and accumulated sleep debt. It
        reflects how much sleep you need to optimize your performance.
      </p>

      <!-- Well-rested nights -->
      <h2 class="sub-title">What are well-rested nights?</h2>

      <div class="grid-2">
        <div class="mini">
          <div class="mini-head">HOURS VS NEEDED</div>
          <div class="mini-body">
            <div class="mini-left">
              <div class="mini-val">90%</div>
              <div class="mini-sub">75 – 90</div>
            </div>
            <div class="mini-ic" aria-hidden="true">
               <IconBalance />
            </div>
          </div>
        </div>

        <div class="mini">
          <div class="mini-head">WELL-RESTED NIGHTS</div>
          <div class="mini-body">
            <div class="mini-left">
              <div class="mini-val">6</div>
              <div class="mini-sub">In last 7d</div>
            </div>
            <div class="mini-ic" aria-hidden="true">
              <IconSleep />
            </div>
          </div>
        </div>
      </div>

      <p class="text-sm accordion-body text-lighter opacity-8">
        Well-rested nights are those when your total sleep hours meet or exceed
        your personal sleep need. This number reflects your sleep patterns over
        the past 7 days. Consistently meeting your sleep needs adds up to your
        well-rested nights and keeps you performing at your best.
      </p>
    </div>
  </div>
</template>

<script>
import IconBed from "@/shared/Controls/Icons/IconBed.vue";
import IconSleep from "@/shared/Controls/Icons/IconSleep.vue";
import IconBalance from "@/shared/Controls/Icons/IconBalance.vue";

export default {
  name: "SleepDebtInfoScreen",
  components: {
    IconSleep,
    IconBalance,
    IconBed,
  },
  data() {
    return {
      // You can bind these later if needed
      sleepDebt: "1h 10m",
      sleepDebtRange: "30m – 1h 30m",
      osd: "8:10",
      strainDelta: "+0:15",
      recentDebt: "+1:35",
      wellRestedCount: "6",
      hoursVsNeededPct: "90%",
      hoursVsNeededRange: "75 – 90",
    };
  },
};
</script>

<style scoped>
.sleepdebt {
  width: 100%;
}

.wrap {
  width: 97%;
  margin: 0 auto;
  padding: 1rem;
}

.title {
  font-size: 1.5rem;
  font-weight: 900;
  letter-spacing: 0.2px;
  margin: 6px 0 14px;
}

.sub-title {
  font-size: 1rem;
  font-weight: bold;
  color: white;
}

/* Top bar */
.debt-bar {
  position: relative;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.05));
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  overflow: hidden;
  margin-bottom: 14px;
}

.accent {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: rgba(255, 191, 0, 1);
}

.debt-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 14px 14px 12px;
  gap: 12px;
}

.left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bed-ic {
  width: 22px;
  height: 22px;
  color: rgba(255, 255, 255, 0.7);
  display: grid;
  place-items: center;
}

.bed-ic svg {
  width: 100%;
  height: 100%;
}

.label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.86);
}

.right {
  text-align: right;
}

.value {
  font-size: 14px;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.9);
}

.range {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
}

/* Calculation card */
.calc-card {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.05));
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  padding: 10px 12px;
  margin-bottom: 12px;
}

.calc-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 2px;
}

.k {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.82);
}

.v {
  font-size: 13px;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.9);
}

.dot {
  width: 7px;
  height: 7px;
  border-radius: 2px;
  display: inline-block;
}

.bg-osd {
  background: rgba(180, 90, 255, 1);
}

.bg-strain {
  background: rgba(255, 60, 60, 1);
}

.bg-debt {
  background: rgba(255, 191, 0, 1);
}

/* Bottom mini cards */
.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin: 10px 0 12px;
}

.mini {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.05));
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  padding: 1rem;
}

.mini-head {
  font-size: 12px;
  letter-spacing: 0.9px;
  color: rgba(255, 255, 255, 0.62);
  font-weight: 900;
}

.mini-body {
  margin-top: 10px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
}

.mini-left {
  display: grid;
  gap: 4px;
}

.mini-val {
  font-size: 22px;
  font-weight: 900;
  letter-spacing: 0.2px;
}

.mini-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

.mini-ic {
  width: 26px;
  height: 26px;
  color: rgba(255, 255, 255, 0.35);
  display: grid;
  place-items: center;
}

.mini-ic svg {
  width: 100%;
  height: 100%;
}

/* Responsive */
@media (max-width: 520px) {
  .grid-2 {
    grid-template-columns: 1fr;
  }
}
</style>
